import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "antd";
import { Calendar, Clock, GraduationCap, Moon, Search, Sun } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import Wordmark from "../../components/Wordmark/Wordmark";
import { TestClock } from "../../components/TestClock/TestClock";
import NotificationBell from "../../components/Notifications/NotificationBell";
import { StatusBadge } from "../../components/StatusBadge/StatusBadge";
import useAssinante from "../../../core/hooks/UseAssinante";
import useUsuario from "../../../core/hooks/UseUsuario";
import useNextSimulado from "../../../core/hooks/useNextSimulado";
import { FOCUS_RING, cx } from "../../components/filters/tokens";
import useDueCount from "../../../core/hooks/useDueCount";

interface TopbarProps {
  hideNav?: boolean;
}

const PILL_CLASS =
  "inline-flex h-[42px] items-center gap-2 whitespace-nowrap rounded-chip border border-ok/40 bg-ok/[0.12] px-3 text-xs font-bold text-ok-ink";

function getInitialDark(): boolean {
  const saved = localStorage.getItem("theme");
  if (saved) return saved === "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/**
 * 72px tall, inset 32px from each edge. No product logo when nav is shown —
 * the Sidebar already carries the wordmark at desktop widths — but hideNav
 * routes (Sidebar absent there) keep a Wordmark linking to /planos,
 * mirroring AppBar.tsx's own "logo always, controls conditional" pattern.
 *
 * When nav is shown, the visual-only search pins to the left edge and
 * everything else — the validity/simulado badges, the Concurso selector,
 * the review pill, the theme toggle and the notification bell, in that
 * order — pins to the right edge via `justify-between` on two flex
 * children, mirroring the frame's search-then-cluster composition (`oREnb`,
 * `r74pDJ`). The bell is appended last as a deliberate deviation: no frame
 * contains it, but nothing else mounts it at desktop width, so dropping it
 * would make notifications unreachable above 768px (proposal.md
 * Assumptions #4).
 *
 * `useNextSimulado` is the same hook Home.view.tsx consumes; its module-level
 * cache (keyed by session) means mounting both on one page still issues a
 * single `GET /mocktests/allbyuser`.
 */
export default function Topbar({ hideNav }: TopbarProps) {
  const navigate = useNavigate();
  const { assinatura, getAssinatura } = useAssinante();
  const { usuario } = useUsuario();
  const nextSimulado = useNextSimulado();
  const due = useDueCount(!hideNav);
  const [isDark, setIsDark] = useState(getInitialDark);
  const certames = assinatura?.selectionProcesses ?? [];

  // Purely local, visual "current selection" — per proposal.md Assumptions #1
  // this selector is informational: it never dispatches a filter/store
  // action and never changes what any screen renders.
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!hideNav) getAssinatura();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideNav]);

  useEffect(() => {
    if (selectedId === undefined && certames.length > 0) {
      setSelectedId(certames[0].id);
    }
  }, [certames, selectedId]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleDarkMode = () => {
    const next = !isDark;
    setIsDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  const selected = certames.find((sp) => sp.id === selectedId) ?? certames[0];
  const selectedLabel = selected?.abbreviation?.toUpperCase() ?? selected?.description ?? "";

  const validUntilLabel = usuario?.validUntil
    ? format(parseISO(usuario.validUntil), "dd/MM/yyyy")
    : null;

  // useNextSimulado already filters to a future start (b7cad80) — no local
  // re-check here, same trust-the-hook pattern Home.view.tsx uses.
  const upcomingSimuladoStart = nextSimulado?.start ?? null;

  return (
    // shrink-0 porque o Topbar é item de um flex column: sem ele o `h-[72px]`
    // é só uma altura-base e o header é comprimido quando o conteúdo é alto —
    // medido em 43px no /home, contra os 72px do /revisao. O AppBar do mobile
    // já tinha o shrink-0; este faltava.
    <div
      data-testid="topbar"
      className="flex h-[72px] shrink-0 items-center justify-between gap-6 border-b border-edge bg-surface px-8"
    >
      {hideNav && (
        /* `/planos` e não `/home`: numa rota `hideNav` o usuário é
           pré-assinatura, e `/home` é `gated(PAID)` — mandá-lo para lá o
           jogaria no <AccessDenied>. Contrato já registrado no CLAUDE.md. */
        <Wordmark width={120} to="/planos" label="Acertei — ver planos" />
      )}

      {!hideNav && (
        <>
          <form
            role="search"
            onSubmit={(e) => e.preventDefault()}
            /*
              ⚠️ Elástica, e é isto que impede o header de cortar controle.

              Antes a busca era `w-[420px] shrink-0`: largura fixa que não
              cedia nunca. Numa barra `flex` de uma linha só, com os controles
              também sem encolher, o que não coubesse na largura transbordava
              para fora e era simplesmente cortado — sem rolagem, sem quebra. E
              a barra estava cheia: selo de validade, data do próximo simulado,
              seletor de certame, cronômetro, pílula de revisão, tema e sino.

              Agora ela estica e encolhe com o espaço que sobra: até 420px (a
              largura de projeto, intacta em tela larga) e a partir de 220px,
              onde o campo ainda se lê. Ceder largura num campo de texto não
              esconde informação nenhuma; cortar um controle, sim.

              220 e não 240: medido na barra CHEIA (validade + próximo simulado
              + certame + pílula + tema + sino) a 768px, o último ponto antes de
              o AppBar assumir — com 240 sobravam 3px de transbordo.
            */
            className="relative min-w-[200px] max-w-[420px] flex-1"
          >
            <Search
              size={16}
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"
            />
            <input
              type="search"
              placeholder="Buscar questões, matérias ou tags…"
              aria-label="Buscar questões, matérias ou tags"
              aria-describedby="topbar-busca-em-breve"
              className="h-[42px] w-full rounded-input border border-edge bg-canvas py-0 pl-9 pr-24 text-sm text-ink placeholder:text-ink-muted transition-colors focus:border-edge-strong focus:outline-none [&::-webkit-search-cancel-button]:appearance-none"
            />
            {/*
              O selo "Em breve" ocupa o lugar do antigo chip `⌘K`.

              A busca é só visual — não há handler nem atalho ligado
              (design.md, Non-Goals). O `⌘K` anunciava um atalho inexistente, o
              que é pior do que não dizer nada; dois chips (um prometendo o
              atalho, outro dizendo que a busca nem existe) se contradiriam.

              Diferente do `⌘K`, este selo NÃO é `aria-hidden`: ele carrega
              informação, e por isso está ligado ao campo por
              `aria-describedby` e usa `text-ink-secondary` (5,48:1 no claro,
              8,15:1 no escuro) em vez de `text-ink-muted`, que mede 2,92:1 e
              reprovaria como texto.
            */}
            <span
              id="topbar-busca-em-breve"
              data-testid="topbar-search-badge"
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded-chip border border-edge bg-surface-muted px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-ink-secondary"
            >
              Em breve
            </span>
          </form>

          {/*
            `min-w-0` para a fileira poder encolher abaixo do seu conteúdo —
            sem ele um filho de flex se recusa a ficar menor que a soma dos
            itens, e o excedente vai para fora da barra em vez de apertar.

            A ordem em que o espaço é cedido, do mais barato para o mais caro:
            1º a busca estica/encolhe (420 → 240); 2º os dois selos de TEXTO
            truncam o próprio valor — "Simulado começa em" sozinho mede 214px,
            mais que o triplo da pílula de revisão. Os controles sem texto
            (pílula, tema, sino) não cedem nada: não há o que truncar neles, e
            encolhê-los só os deformaria.
          */}
          <div className="flex min-w-0 items-center gap-4">
            {validUntilLabel && (
              <StatusBadge
                icon={Calendar}
                iconClassName="bg-accent-blue/[0.12] text-accent-blue-ink"
                caption="Válido até"
                value={validUntilLabel}
              />
            )}

            {upcomingSimuladoStart && (
              /*
                O selo leva à inscrição do simulado que ele anuncia.
                `/inscricao/:tipo` filtra a lista pelo tipo e mostra os
                disponíveis — é lá que o aluno se inscreve. Sem `type` no
                simulado, `completa` lista todos, que é o destino mais
                próximo possível em vez de nenhum.
              */
              <button
                type="button"
                data-testid="topbar-proximo-simulado"
                onClick={() =>
                  navigate(`/inscricao/${nextSimulado?.type ?? "completa"}`, {
                    state: nextSimulado?.type ?? "completa",
                  })
                }
                title={`Inscrever-se em ${nextSimulado?.description ?? "simulado"}`}
                aria-label={`Simulado começa em ${format(parseISO(upcomingSimuladoStart), "dd 'de' MMMM 'às' HH:mm", { locale: ptBR })}. Abrir inscrição.`}
                // `min-w-0` + `max-w`: o `StatusBadge` já sabe truncar por
                // dentro (`overflow-hidden` + `truncate` nas duas linhas), mas
                // nunca tinha a chance — a cadeia de pais toda tinha largura
                // automática. O `title` acima mantém o valor inteiro
                // alcançável depois do corte.
                className={cx("min-w-0 max-w-[214px] shrink rounded-chip transition-colors hover:bg-surface-hover", FOCUS_RING)}
              >
                <StatusBadge
                  icon={Clock}
                  iconClassName="bg-accent-purple/[0.12] text-accent-purple-ink"
                  caption="Simulado começa em"
                  value={format(parseISO(upcomingSimuladoStart), "dd 'de' MMMM 'às' HH:mm", {
                    locale: ptBR,
                  })}
                />
              </button>
            )}

            {certames.length > 0 && (
              <Dropdown
                trigger={["click"]}
                menu={{
                  selectedKeys: selectedId ? [selectedId] : [],
                  onClick: ({ key }) => setSelectedId(key),
                  items: certames.map((sp, idx) => ({
                    key: sp.id ?? String(idx),
                    label: sp.abbreviation?.toUpperCase() ?? sp.description ?? sp.id,
                  })),
                }}
              >
                {/* `min-w-[124px]`: o valor aqui é a SIGLA do certame ("CBMGO"),
                    curta por natureza, e abaixo de ~1100px numa barra cheia ela
                    chega a truncar para "CO…", que é feio.

                    ⚠️ Tentei dar-lhe um piso (124px, depois 96px) para evitar
                    isso: nos DOIS casos algum controle voltou a sair da barra
                    a 820 e 768px — medido, não suposto. Entre um rótulo
                    abreviado e um controle que some, o rótulo perde: ele
                    continua na tela, com o `title` ao lado trazendo o valor
                    inteiro. Não reintroduza o piso sem medir essas duas
                    larguras. */}
                <button type="button" title="Concurso" className="min-w-0 max-w-[149px]">
                  <StatusBadge
                    icon={GraduationCap}
                    iconClassName="bg-accent-green/[0.12] text-accent-green-ink"
                    caption="Concurso"
                    value={selectedLabel}
                    chevron
                    className="transition-colors hover:bg-surface-hover"
                  />
                </button>
              </Dropdown>
            )}

            {/*
              O cronômetro do simulado.
              ⚠️ Ele NÃO é só um mostrador: é o `TestClock` que abre o
              `/api/ws` e, ao receber `tick`, redireciona para
              `/simulado-iniciado`. É isso que faz o simulado começar na hora
              marcada para quem está em qualquer outra tela — por isso ele mora
              no header compartilhado, e não só no layout do simulado.
              Ver `components/TestClock/TestClock.tsx`.
            */}
            <TestClock time="00:00:00" />

            <button
              type="button"
              data-testid="review-pill"
              onClick={() => navigate("/revisao")}
              title="Revisão"
              className={`${PILL_CLASS} shrink-0 transition-colors hover:bg-ok/[0.18]`}
            >
              {due > 0 ? `${due} para revisar` : "Revisar"}
            </button>

            <button
              type="button"
              onClick={toggleDarkMode}
              aria-label={isDark ? "Ativar modo claro" : "Ativar modo escuro"}
              title={isDark ? "Modo claro" : "Modo escuro"}
              className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-input border border-edge bg-surface text-ink-secondary transition-colors hover:bg-surface-hover hover:text-ink"
            >
              {isDark ? (
                <Sun size={18} strokeWidth={1.75} aria-hidden="true" />
              ) : (
                <Moon size={18} strokeWidth={1.75} aria-hidden="true" />
              )}
            </button>

            <NotificationBell enabled={!hideNav} />
          </div>
        </>
      )}
    </div>
  );
}
